/**
 * Design tokens — single source of truth for color, type, motion, spacing.
 *
 * Tokens are imported by `theme.ts` and exposed on `theme.palette.*` etc.
 * Components MUST consume tokens via the MUI theme (not these constants
 * directly) so dark-mode and accent rebrands compile to one diff.
 *
 * Naming:
 *   - `brand.*`    portal-agnostic identity (logo, primary actions)
 *   - `accent.*`   secondary accent for charts / highlights
 *   - `surface.*`  background hierarchy (canvas → card → elevated)
 *   - `text.*`     typographic hierarchy
 *   - `status.*`   semantic states (success / warning / error / info)
 */

export const palette = {
  brand: {
    deep: '#0a3d3a',
    dark: '#0f5b56',
    main: '#127a72',
    lighter: '#e6f4f3',
    contrastText: '#ffffff',
  },
  accent: {
    dark: '#b45309',
    main: '#d97706',
    lighter: '#fef3c7',
    contrastText: '#1a1208',
  },
  surface: {
    canvas: '#f6f7f9',
    card: '#ffffff',
    elevated: '#ffffff',
    sunken: '#eceef2',
    inverse: '#0f1115',
  },
  text: {
    primary: '#0f1115',
    secondary: '#4a5060',
    tertiary: '#7a8090',
    onInverse: '#f6f7f9',
    onBrand: '#ffffff',
    disabled: '#aab0bd',
  },
  border: {
    subtle: '#e4e7ec',
    strong: '#d0d5dd',
    focus: '#127a72',
  },
  status: {
    success: '#0f7a4a',
    successBg: '#e6f4ef',
    warning: '#9a6700',
    warningBg: '#fff8db',
    danger: '#a3261c',
    dangerBg: '#fdecea',
    info: '#1a4f9c',
    infoBg: '#e6effc',
  },
} as const;

/** Spacing — 4px base. Use multiples (theme.spacing(2) = 8px). */
export const spacing = 4;

/** Corner radii — keep small. Premium ≠ rounded. */
export const radius = {
  xs: 4,
  sm: 6,
  md: 10,
  lg: 14,
  pill: 999,
} as const;

/** Type scale. Inter for body; JetBrains Mono only for code/IDs. */
export const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  fontFamilyMono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  display: { size: 36, lineHeight: 1.15, weight: 600, tracking: -0.4 },
  h1:      { size: 28, lineHeight: 1.2,  weight: 600, tracking: -0.3 },
  h2:      { size: 22, lineHeight: 1.25, weight: 600, tracking: -0.2 },
  h3:      { size: 18, lineHeight: 1.3,  weight: 600, tracking: -0.1 },
  body:    { size: 14, lineHeight: 1.5,  weight: 400, tracking: 0    },
  bodyLg:  { size: 16, lineHeight: 1.55, weight: 400, tracking: 0    },
  caption: { size: 12, lineHeight: 1.4,  weight: 500, tracking: 0.2  },
  micro:   { size: 11, lineHeight: 1.3,  weight: 600, tracking: 0.6  },
} as const;

/** Elevation — flatter than Material default; premium feel is depth via border + soft shadow, not blur. */
export const elevation = {
  flat: 'none',
  card: '0 1px 2px rgba(15, 17, 21, 0.04), 0 0 0 1px rgba(15, 17, 21, 0.06)',
  raised: '0 4px 12px rgba(15, 17, 21, 0.06), 0 0 0 1px rgba(15, 17, 21, 0.06)',
  overlay: '0 12px 32px rgba(15, 17, 21, 0.16), 0 0 0 1px rgba(15, 17, 21, 0.08)',
  focusRing: '0 0 0 3px rgba(18, 122, 114, 0.20)',
} as const;

/** Motion — durations in ms, easings as cubic-bezier. */
export const motion = {
  durations: {
    instant: 80,
    fast: 140,
    medium: 220,
    slow: 320,
  },
  easings: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.3, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
  },
} as const;

/** Z-stack — keep one canonical order. */
export const zIndex = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
  commandPalette: 1600,
} as const;
